import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ConfirmationConfig } from '../components/shared/confirmation-modal/confirmation-modal.component';

export interface FormConfirmationConfig extends ConfirmationConfig {
  formType: 'cancel' | 'submit' | 'reset' | 'navigate' | 'data-loss' | 'delete';
  unsavedChanges?: boolean;
  formName?: string;
  criticalData?: string[];
}

export interface FormChangeTracker {
  hasUnsavedChanges(): boolean;
  getFormName(): string;
  getCriticalData?(): string[];
}

/**
 * Servicio centralizado para manejar confirmaciones en formularios
 * Aplica el principio SRP (Single Responsibility Principle)
 */
@Injectable({
  providedIn: 'root'
})
export class ConfirmationService {

  private confirmationSubject = new Subject<{ config: ConfirmationConfig, resolve: (result: boolean) => void }>();
  public confirmation$ = this.confirmationSubject.asObservable();

  constructor() { }

  /**
   * Muestra un modal de confirmación y retorna el resultado
   */
  private showConfirmation(config: ConfirmationConfig): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmationSubject.next({ config, resolve });
    });
  }

  /**
   * Configuración para cancelar formulario con cambios no guardados
   */
  getFormCancelConfig(formName: string = 'el formulario'): FormConfirmationConfig {
    return {
      formType: 'cancel',
      title: '¿Descartar cambios?',
      message: `Tienes cambios sin guardar en ${formName} que se perderán permanentemente.`,
      type: 'warning',
      icon: 'fa-exclamation-triangle',
      confirmText: 'Sí, descartar cambios',
      cancelText: 'Continuar editando',
      showIcon: true,
      formName
    };
  }

  /**
   * Configuración para envío de formulario crítico
   */
  getFormSubmitConfig(formName: string, criticalData: string[]): FormConfirmationConfig {
    const dataPreview = criticalData.length > 0 ?
      `\n\nDatos principales:\n${criticalData.join('\n')}` : '';

    return {
      formType: 'submit',
      title: 'Confirmar envío',
      message: `Estás a punto de crear ${formName}. Esta acción no se puede deshacer.${dataPreview}`,
      type: 'info',
      icon: 'fa-info-circle',
      confirmText: 'Sí, crear',
      cancelText: 'Revisar datos',
      showIcon: true,
      formName,
      criticalData
    };
  }

  /**
   * Configuración para reset/limpiar formulario
   */
  getFormResetConfig(formName: string = 'el formulario'): FormConfirmationConfig {
    return {
      formType: 'reset',
      title: '¿Limpiar formulario?',
      message: `Se eliminarán todos los datos ingresados en ${formName}. Esta acción no se puede deshacer.`,
      type: 'warning',
      icon: 'fa-exclamation-triangle',
      confirmText: 'Sí, limpiar todo',
      cancelText: 'Cancelar',
      showIcon: true,
      formName
    };
  }

  /**
   * Configuración para navegación con cambios no guardados
   */
  getNavigationConfig(fromRoute: string): FormConfirmationConfig {
    return {
      formType: 'navigate',
      title: '¿Salir sin guardar?',
      message: 'Tienes cambios sin guardar que se perderán si sales de esta página.',
      type: 'warning',
      icon: 'fa-exclamation-triangle',
      confirmText: 'Sí, salir sin guardar',
      cancelText: 'Permanecer aquí',
      showIcon: true
    };
  }

  /**
   * Configuración para eliminar elementos críticos
   */
  getDeleteConfig(itemName: string, itemDescription?: string): FormConfirmationConfig {
    return {
      formType: 'delete',
      title: `¿Eliminar ${itemName}?`,
      message: `Esta acción eliminará permanentemente ${itemDescription || itemName}. No se puede deshacer.`,
      type: 'danger',
      icon: 'fa-trash',
      confirmText: 'Sí, eliminar',
      cancelText: 'Cancelar',
      showIcon: true
    };
  }

  /**
   * Configuración para cambios que implican pérdida de datos
   */
  getDataLossConfig(changeDescription: string): FormConfirmationConfig {
    return {
      formType: 'data-loss',
      title: '¿Continuar con el cambio?',
      message: `${changeDescription}\n\nEsta acción eliminará los datos relacionados y no se puede deshacer.`,
      type: 'warning',
      icon: 'fa-exclamation-triangle',
      confirmText: 'Sí, continuar',
      cancelText: 'Cancelar',
      showIcon: true
    };
  }

  // === MÉTODOS ESPECÍFICOS PARA CADA TIPO DE FORMULARIO ===

  /**
   * Confirmación para cancelar/navegar con cambios no guardados
   */
  async confirmFormCancel(tracker: FormChangeTracker): Promise<boolean> {
    if (!tracker.hasUnsavedChanges()) {
      return true; // No hay cambios, permitir navegación
    }

    const config = this.getFormCancelConfig(tracker.getFormName());
    return await this.showConfirmation(config);
  }

  /**
   * Confirmación para envío de formulario con datos críticos
   */
  async confirmFormSubmit(tracker: FormChangeTracker): Promise<boolean> {
    const criticalData = tracker.getCriticalData?.() || [];
    const config = this.getFormSubmitConfig(tracker.getFormName(), criticalData);
    return await this.showConfirmation(config);
  }

  /**
   * Confirmación para reset de formulario
   */
  async confirmFormReset(formName: string): Promise<boolean> {
    const config = this.getFormResetConfig(formName);
    return await this.showConfirmation(config);
  }

  /**
   * Confirmación para navegación (usado por guards)
   */
  async confirmNavigation(fromRoute: string): Promise<boolean> {
    const config = this.getNavigationConfig(fromRoute);
    return await this.showConfirmation(config);
  }

  /**
   * Confirmación para eliminar elementos
   */
  async confirmDelete(itemName: string, itemDescription?: string): Promise<boolean> {
    const config = this.getDeleteConfig(itemName, itemDescription);
    return await this.showConfirmation(config);
  }

  /**
   * Confirmación para cambios que causan pérdida de datos
   */
  async confirmDataLoss(changeDescription: string): Promise<boolean> {
    const config = this.getDataLossConfig(changeDescription);
    return await this.showConfirmation(config);
  }

  // === CONFIGURACIONES ESPECÍFICAS POR MÓDULO ===

  /**
   * Configuraciones específicas para el módulo de devoluciones
   */
  refund = {
    changeType: (fromType: string, toType: string) => this.confirmDataLoss(
      `Cambiar de "${fromType}" a "${toType}" eliminará todas las selecciones actuales.`
    ),

    changeEnrollmentType: (fromType: string, toType: string) => this.confirmDataLoss(
      `Cambiar de "${fromType}" a "${toType}" reiniciará todos los datos del formulario.`
    ),

    largeAmount: (amount: number) => this.showConfirmation({
      title: 'Monto elevado detectado',
      message: `El monto de devolución es ${amount.toLocaleString('es-CO', { style: 'currency', currency: 'COP' })}. ¿Confirmas que es correcto?`,
      type: 'warning',
      confirmText: 'Sí, es correcto',
      cancelText: 'Revisar monto',
      showIcon: true
    })
  };

  /**
   * Configuraciones específicas para comprobantes de ingreso
   */
  incomeReceipt = {
    changePayerType: (fromType: string, toType: string) => this.confirmDataLoss(
      `Cambiar de "${fromType}" a "${toType}" reiniciará la selección de participante y planes de pago.`
    ),

    generatePaymentLink: (amount: number) => this.showConfirmation({
      title: 'Generar enlace de pago',
      message: `Se generará un enlace de pago por ${amount.toLocaleString('es-CO', { style: 'currency', currency: 'COP' })}. ¿Continuar?`,
      type: 'info',
      confirmText: 'Sí, generar enlace',
      cancelText: 'Cancelar',
      showIcon: true
    })
  };

  /**
   * Configuraciones específicas para inscripciones
   */
  enrollment = {
    changeType: (fromType: string, toType: string) => this.confirmDataLoss(
      `Cambiar de "${fromType}" a "${toType}" eliminará todos los datos ingresados.`
    ),

    removePaymentPlan: (installmentNumber: number) => this.confirmDelete(
      'cuota de pago',
      `la cuota ${installmentNumber} del plan de pagos`
    ),

    highValueBusinessEnrollment: (totalAmount: number, participantCount: number) => this.showConfirmation({
      title: 'Inscripción empresarial de alto valor',
      message: `Está creando una inscripción corporativa por ${totalAmount.toLocaleString('es-CO', { style: 'currency', currency: 'COP' })} para ${participantCount} participante${participantCount > 1 ? 's' : ''}. Por favor, verifique que todos los datos sean correctos.`,
      type: 'warning',
      confirmText: 'Sí, crear inscripción',
      cancelText: 'Revisar datos',
      showIcon: true,
      icon: 'fa-building'
    }),

    internationalEnrollmentWithExchangeRate: (originalAmount: number, currencyCode: string, copEquivalent: number, exchangeRate: number) => this.showConfirmation({
      title: 'Inscripción internacional con conversión de moneda',
      message: `Monto original: ${originalAmount.toLocaleString('en-US', { style: 'currency', currency: currencyCode })}\nEquivalente en COP: ${copEquivalent.toLocaleString('es-CO', { style: 'currency', currency: 'COP' })}\nTasa de cambio: 1 ${currencyCode} = ${exchangeRate.toFixed(2)} COP\n\n¿Confirma que los datos de conversión son correctos?`,
      type: 'info',
      confirmText: 'Sí, crear inscripción',
      cancelText: 'Verificar tasa',
      showIcon: true,
      icon: 'fa-exchange-alt'
    }),

    /**
     * Confirmación para cambio de estado de inscripción
     */
    statusChange: (statusLabel: string, enrollmentType: 'individual' | 'corporate' = 'individual') => this.showConfirmation({
      title: 'Confirmar cambio de estado',
      message: `¿Está seguro de cambiar el estado de la inscripción ${enrollmentType === 'corporate' ? 'corporativa' : ''} a "${statusLabel}"?`,
      type: 'warning',
      confirmText: 'Sí, cambiar estado',
      cancelText: 'Cancelar',
      showIcon: true,
      icon: 'fa-exchange-alt'
    }),

    /**
     * Confirmación para cambio de estado que requiere razón (Withdrawn, Deferred)
     */
    statusChangeWithReason: (statusLabel: string) => this.showConfirmation({
      title: `Cambiar a "${statusLabel}"`,
      message: `Este cambio de estado requiere una justificación. ¿Desea continuar?`,
      type: 'warning',
      confirmText: 'Sí, continuar',
      cancelText: 'Cancelar',
      showIcon: true,
      icon: 'fa-edit'
    })
  };

  /**
   * Configuraciones específicas para órdenes de trabajo
   */
  workOrder = {
    removeItem: (itemDescription: string) => this.confirmDelete(
      'elemento',
      `el elemento "${itemDescription}" de la orden de trabajo`
    ),

    exceedsBudget: (amount: number, budget: number) => this.showConfirmation({
      title: 'Presupuesto excedido',
      message: `El monto total (${amount.toLocaleString('es-CO', { style: 'currency', currency: 'COP' })}) excede el presupuesto disponible (${budget.toLocaleString('es-CO', { style: 'currency', currency: 'COP' })}). ¿Continuar de todos modos?`,
      type: 'warning',
      confirmText: 'Sí, continuar',
      cancelText: 'Revisar montos',
      showIcon: true
    })
  };

  /**
   * Configuraciones específicas para comprobantes de egreso
   */
  expenseReceipt = {
    removeItem: (itemDescription: string) => this.confirmDelete(
      'comprobante',
      `el comprobante "${itemDescription}"`
    ),

    largeExpense: (amount: number) => this.showConfirmation({
      title: 'Gasto elevado detectado',
      message: `El monto del gasto es ${amount.toLocaleString('es-CO', { style: 'currency', currency: 'COP' })}. ¿Confirmas que es correcto?`,
      type: 'warning',
      confirmText: 'Sí, es correcto',
      cancelText: 'Revisar monto',
      showIcon: true
    })
  };
}
